import { request, windowQuery, type DateWindow } from './client'
import type { StaffAuth } from './teaching'
import type { ContentState, ReleaseAudience, Skill } from '../types/prisma-enums'

/**
 * *"Which games have these children played, how much, and how well?"* — the same
 * question at four scopes.
 *
 * 🔴 ONE TYPE, FOUR ROUTES, AND THAT IS THE POINT. `architecture.md` §12 —
 * *"define each metric once, scope-filter it per tier"* — is enforced on the
 * server by a single `GameStatsService`; this file is the client half of the
 * same rule. Four differently-shaped responses would let four screens drift into
 * four vocabularies for the same five games, and the drift would surface as a
 * head teacher's total not matching the sum of the class panels underneath it.
 *
 * ⚠️ NOTHING HERE IS A CHILD. Every field is a count, a whole percentage or a
 * date over a set of players the SERVER scoped — a teacher's own classes, a
 * school's own roster, one parent's own child. The client never narrows
 * anything and must never appear to.
 */

/** One game, over whatever set of players the route scoped. */
export interface GameStat {
  /** The id the events table records — "sky-quest". `GAME_TITLES` names it. */
  game: string
  /**
   * 🔴 DISTINCT CHILDREN WHO HAVE PLAYED IT, NOT A SHARE. The denominator comes
   * with the response (`childCount`) rather than being baked in, because the set
   * the server scoped is the only thing that knows how big it is.
   */
  children: number
  /** Levels finished. */
  rounds: number
  correct: number
  wrong: number
  /**
   * A whole percentage, or `null` when nothing has been answered.
   *
   * ⚠️ `null` IS NOT ZERO. "Nobody has answered a question" and "everybody got
   * them wrong" are opposite facts, and a screen that rendered a zero would show
   * the second.
   */
  accuracyPct: number | null
  /** The highest level anybody in the set has **completed**. Never attempted. */
  topLevel: number | null
  lastPlayedAt: string | null
}

/**
 * The headline above the table.
 *
 * ⚠️ IT IS COMPUTED SERVER-SIDE FROM THE SAME ROWS, so a tile can never
 * contradict the table under it — `design-system.md` §8. Deriving it in the
 * browser is exactly how the two come apart.
 */
export interface GameSummary {
  gamesPlayed: number
  gamesAvailable: number
  rounds: number
  accuracyPct: number | null
  lastPlayedAt: string | null
  mostPlayed: string | null
}

export interface GameStatsResponse {
  games: GameStat[]
  summary: GameSummary
}

/** A class. `childCount` is the denominator — "12 of 24 played Sky Quest". */
export type ClassGames = GameStatsResponse & {
  classId: string
  className: string
  childCount: number
}

/** A whole school. Same shape, wider set. */
export type SchoolGames = GameStatsResponse & { childCount: number }

/**
 * The platform, over a window.
 *
 * 🔴 `windowDays` TRAVELS WITH THE ROWS SO A SCREEN CANNOT DRAW THE NUMBER
 * WITHOUT THE PERIOD IT COVERS — OD-39/OD-72's denominator rule, applied to
 * time. "Nobody played Bridge Patterns" and "nobody played Bridge Patterns this
 * month" are different findings.
 */
export type PlatformGames = GameStatsResponse & { windowDays: number }

/** `GET /classes/:classId/games` — the teacher's class panel. */
export const getClassGames = (classId: string, auth: StaffAuth = {}): Promise<ClassGames> =>
  request<ClassGames>(`/classes/${encodeURIComponent(classId)}/games`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `GET /players/:playerId/games` — one child.
 *
 * 🔴 A PARENT MAY CALL THIS AND MAY NOT CALL `/strengths`, AND THE DIFFERENCE IS
 * WHAT THE ANSWER IS MADE OF. Strengths compare a child against their
 * classmates, so they are partly an aggregate of other people's children — the
 * parent tier refuses that on principle. This is built from one child's own
 * event stream and contains no comparison with anybody.
 */
export const getPlayerGames = (
  playerId: string,
  auth: StaffAuth = {},
): Promise<GameStatsResponse> =>
  request<GameStatsResponse>(`/players/${encodeURIComponent(playerId)}/games`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/** `GET /school/games` — every class in the school. No `:schoolId`: it comes
    from the token (§8.1 — *"an admin who can name their own school can name any
    school"*). */
export const getSchoolGames = (auth: StaffAuth = {}): Promise<SchoolGames> =>
  request<SchoolGames>('/school/games', {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `GET /superadmin/games` — the whole platform, over the server's window or an
 * explicit one.
 *
 * ⚠️ `windowDays` COMES BACK EITHER WAY and names what was counted, so the
 * screen prints the period beside the figure instead of assuming thirty days.
 */
export const getPlatformGames = (
  auth: StaffAuth = {},
  window?: DateWindow,
): Promise<PlatformGames> =>
  request<PlatformGames>(`/superadmin/games${windowQuery(window)}`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/* ── K10 · the per-game catalogue, releases and question bank (PKG-PLATFORM) ── */

/*
 * `GET/PATCH /platform/games…`, `/platform/game-releases`,
 * `/platform/content-items…` — operator only. Reads need `platform.read`;
 * every write `platform.settings` and is audited (`GAME_RELEASE_CHANGE`). The
 * server is the control; the screen only hides what a duty cannot do.
 */

interface OperatorAuth {
  token: string
  signal?: AbortSignal
}

/** One game as the catalogue holds it. */
export interface CatalogueGame {
  id: string
  name: string
  /** `Game.subjectLabel`, or Figma's subject until an operator writes one. */
  subject: string
  subjectIsDefault: boolean
  /** Who may see it: DRAFT = Kalqy only, BETA = beta schools, PUBLISHED = every school. */
  state: ContentState
  /** Schools may try it before they pay. */
  trialAllowed: boolean
  sortOrder: number
  /** The newest PUBLISHED release. */
  liveVersion: string | null
  liveReleasedAt: string | null
  /** The newest BETA release. */
  betaVersion: string | null
  /** Schools where a child finished a round of it in the last `reachDays` days. */
  reachSchools: number
  legalNextStates: readonly ContentState[]
  updatedAt: string
}

export interface GameCatalogue {
  games: CatalogueGame[]
  /** Schools with the `content.beta` flag on. */
  betaSchools: number
  reachDays: number
}

export interface GameReleaseView {
  id: string
  gameId: string
  gameName: string
  version: string
  state: ContentState
  audience: ReleaseAudience
  notes: string | null
  publishedAt: string | null
  retiredAt: string | null
  createdAt: string
  legalNextStates: readonly ContentState[]
}

export interface GameReleasePage {
  rows: GameReleaseView[]
  nextCursor: string | null
  betaSchools: number
}

export interface GameReleaseQuery {
  gameId?: string
  state?: ContentState
  /** Inclusive IST days on when the release was cut; omit both for all time. */
  from?: string
  to?: string
  limit?: number
  cursor?: string
}

export interface ContentItemView {
  id: string
  gameId: string
  level: number
  skill: Skill | null
  prompt: string
  ncfCode: string | null
  contentVersion: string | null
  retiredAt: string | null
  updatedAt: string
}

export interface ContentItemPage {
  rows: ContentItemView[]
  nextCursor: string | null
}

export interface ContentItemQuery {
  gameId?: string
  level?: number
  q?: string
  includeRetired?: boolean
  limit?: number
  cursor?: string
}

export interface ContentItemPatch {
  prompt?: string
  level?: number
  skill?: Skill | null
  ncfCode?: string | null
  retired?: boolean
}

function gamesQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v))
  const s = q.toString()
  return s ? `?${s}` : ''
}

const opGet = <T>(path: string, auth: OperatorAuth): Promise<T> =>
  request<T>(path, { context: 'user', token: auth.token, signal: auth.signal })

const opSend = <T>(method: 'POST' | 'PATCH', path: string, body: unknown, auth: OperatorAuth): Promise<T> =>
  request<T>(path, { method, body, context: 'user', token: auth.token, signal: auth.signal })

const g = encodeURIComponent

export const getGameCatalogue = (auth: OperatorAuth): Promise<GameCatalogue> => opGet('/platform/games', auth)

/** State (who may see it), the test-game flag, or the subject (`''`/null → default). */
export const updateGame = (
  id: string,
  body: { state?: ContentState; trialAllowed?: boolean; subjectLabel?: string | null },
  auth: OperatorAuth,
): Promise<CatalogueGame> => opSend('PATCH', `/platform/games/${g(id)}`, body, auth)

export const getGameReleases = (query: GameReleaseQuery, auth: OperatorAuth): Promise<GameReleasePage> =>
  opGet(`/platform/game-releases${gamesQuery({ ...query })}`, auth)

/** Always cut as a draft; a version is never reused (409). */
export const createGameRelease = (
  gameId: string,
  body: { version: string; notes?: string; audience?: ReleaseAudience },
  auth: OperatorAuth,
): Promise<GameReleaseView> => opSend('POST', `/platform/games/${g(gameId)}/releases`, body, auth)

/** Publishing retires the game's previous live version (and older betas) on the server. */
export const updateGameRelease = (
  gameId: string,
  releaseId: string,
  body: { state?: ContentState; audience?: ReleaseAudience; notes?: string | null },
  auth: OperatorAuth,
): Promise<GameReleaseView> => opSend('PATCH', `/platform/games/${g(gameId)}/releases/${g(releaseId)}`, body, auth)

export const getContentItems = (query: ContentItemQuery, auth: OperatorAuth): Promise<ContentItemPage> =>
  opGet(`/platform/content-items${gamesQuery({ ...query })}`, auth)

/** A8 "Edit question". */
export const updateContentItem = (id: string, body: ContentItemPatch, auth: OperatorAuth): Promise<ContentItemView> =>
  opSend('PATCH', `/platform/content-items/${g(id)}`, body, auth)
